import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, Copy } from 'lucide-react';
import { NotionDrawer } from '../ui/notion/NotionDrawer';
import {
  linkRaccolta,
  useDocumentCollectionStore,
} from '../../lib/stores/documentCollectionStore';
import {
  copiaVoci,
  useDocumentRequestTemplateStore,
  vociPulite,
  type ModelloDocumenti,
} from '../../lib/stores/documentRequestTemplateStore';
import type { LeadFormDocumentRequirement } from '../../lib/types/leadForm';
import ElencoDocumentiEditor, { inputDocumentiCls, nuovaVoce } from './ElencoDocumentiEditor';
import SceltaElencoDocumenti, { caricaModuliConDocumenti } from './SceltaElencoDocumenti';

/**
 * "Richiedi documenti": apre una richiesta e restituisce il collegamento da
 * mandare.
 *
 * Si parte da un modello (l'elenco preparato una volta), dall'elenco di un
 * modulo che ha gia' il campo "Documenti richiesti", oppure da un elenco
 * nuovo, che si puo' salvare come modello per la volta dopo. L'elenco scelto
 * resta modificabile per questa richiesta sola.
 */

interface Props {
  aggancio: { client_id?: string; project_id?: string; deal_id?: string };
  onChiudi: () => void;
}

type Fase = 'scelta' | 'elenco' | 'fatto';

/** Da dove arriva l'elenco: cambia solo il sottotitolo e l'offerta di salvarlo come modello. */
type Origine = { tipo: 'modello' | 'modulo'; nome: string } | null;

export default function RichiediDocumentiModal({ aggancio, onChiudi }: Props) {
  const crea = useDocumentCollectionStore((s) => s.crea);
  const { modelli, caricati, carica: caricaModelli, salva: salvaModello } = useDocumentRequestTemplateStore();

  const [fase, setFase] = useState<Fase>('scelta');
  const [origine, setOrigine] = useState<Origine>(null);
  const [moduli, setModuli] = useState<ModelloDocumenti[]>([]);
  const [moduliCaricati, setModuliCaricati] = useState(false);
  const [voci, setVoci] = useState<LeadFormDocumentRequirement[]>([nuovaVoce()]);
  const [giorni, setGiorni] = useState(30);
  const [salvaComeModello, setSalvaComeModello] = useState(false);
  const [nomeModello, setNomeModello] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [linkCreato, setLinkCreato] = useState<string | null>(null);
  const [copiato, setCopiato] = useState(false);

  useEffect(() => {
    if (!caricati) void caricaModelli();
  }, [caricati, caricaModelli]);

  useEffect(() => {
    let attivo = true;
    void caricaModuliConDocumenti().then((m) => {
      if (!attivo) return;
      setModuli(m);
      setModuliCaricati(true);
    });
    return () => { attivo = false; };
  }, []);

  // Senza modelli ne' moduli non c'e' niente da scegliere: si va dritti all'elenco.
  useEffect(() => {
    if (caricati && moduliCaricati && modelli.length === 0 && moduli.length === 0 && fase === 'scelta') {
      setFase('elenco');
    }
  }, [caricati, moduliCaricati, modelli.length, moduli.length, fase]);

  const scegli = (modello: ModelloDocumenti | null, tipo: 'modello' | 'modulo' = 'modello') => {
    setOrigine(modello ? { tipo, nome: modello.name } : null);
    setVoci(modello ? copiaVoci(modello.requirements) : [nuovaVoce()]);
    setGiorni(modello?.giorni_validita ?? 30);
    setSalvaComeModello(false);
    setNomeModello('');
    setFase('elenco');
  };

  const vociValide = useMemo(() => vociPulite(voci), [voci]);

  const conferma = useCallback(async () => {
    if (vociValide.length === 0) {
      setErrore('Aggiungi almeno un documento da richiedere.');
      return;
    }
    if (salvaComeModello && !nomeModello.trim()) {
      setErrore('Dai un nome al modello, così lo ritrovi.');
      return;
    }
    setInCorso(true);
    setErrore(null);

    if (salvaComeModello) {
      await salvaModello({ name: nomeModello, requirements: vociValide, giorni_validita: giorni });
    }

    const raccolta = await crea(aggancio, vociValide, giorni);
    setInCorso(false);
    if (!raccolta) {
      setErrore('Non è stato possibile creare la richiesta. Riprova.');
      return;
    }
    setLinkCreato(linkRaccolta(raccolta.token));
    setFase('fatto');
  }, [aggancio, crea, giorni, nomeModello, salvaComeModello, salvaModello, vociValide]);

  const copia = useCallback(async () => {
    if (!linkCreato) return;
    try {
      await navigator.clipboard.writeText(linkCreato);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    } catch {
      window.prompt('Copia il collegamento:', linkCreato);
    }
  }, [linkCreato]);

  const pulsante = 'px-3 py-1.5 text-sm rounded-md';

  return (
    <NotionDrawer
      isOpen
      onClose={onChiudi}
      title="Richiedi documenti"
      subtitle={
        fase === 'scelta'
          ? 'Scegli un elenco pronto oppure scrivine uno nuovo.'
          : fase === 'elenco'
            ? origine
              ? `Dal ${origine.tipo} "${origine.nome}": puoi ritoccarlo solo per questa richiesta.`
              : 'Il cliente li carica da un collegamento, uno alla volta.'
            : undefined
      }
      width="lg"
      footer={
        fase === 'fatto' ? (
          <div className="flex justify-end">
            <button type="button" onClick={onChiudi} className={`${pulsante} bg-[#37352F] text-white hover:bg-black`}>
              Chiudi
            </button>
          </div>
        ) : fase === 'elenco' ? (
          <div className="flex items-center justify-between">
            {errore ? <span className="text-xs text-red-600">{errore}</span> : <span />}
            <div className="flex gap-2">
              <button type="button" onClick={onChiudi} className={`${pulsante} text-[#787774] hover:bg-[rgba(55,53,47,0.06)]`}>
                Annulla
              </button>
              <button
                type="button"
                onClick={() => void conferma()}
                disabled={inCorso}
                className={`${pulsante} bg-[#37352F] text-white hover:bg-black disabled:opacity-50`}
              >
                {inCorso ? 'Creo…' : 'Crea collegamento'}
              </button>
            </div>
          </div>
        ) : undefined
      }
    >
      {fase === 'scelta' && (
        <SceltaElencoDocumenti modelli={modelli} moduli={moduli} onScegli={scegli} />
      )}

      {fase === 'elenco' && (
        <div className="space-y-4">
          {(modelli.length > 0 || moduli.length > 0) && (
            <button
              type="button"
              onClick={() => setFase('scelta')}
              className="flex items-center gap-1 text-xs text-[#787774] hover:text-[#37352F]"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Scegli un altro elenco
            </button>
          )}

          <ElencoDocumentiEditor voci={voci} onChange={setVoci} />

          <label className="block">
            <span className="mb-1 block text-xs text-[#787774]">Il collegamento scade dopo</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={giorni}
                onChange={(e) => setGiorni(Math.max(0, Number(e.target.value) || 0))}
                className={`${inputDocumentiCls} w-24`}
              />
              <span className="text-xs text-[#787774]">giorni (0 = mai)</span>
            </div>
          </label>

          {origine?.tipo !== 'modello' && (
            <div className="space-y-2 rounded-lg bg-[rgba(55,53,47,0.03)] px-3 py-2.5">
              <label className="flex items-center gap-2 text-sm text-[#37352F] dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={salvaComeModello}
                  onChange={(e) => setSalvaComeModello(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Salva questo elenco come modello
              </label>
              {salvaComeModello && (
                <input
                  className={inputDocumentiCls}
                  value={nomeModello}
                  onChange={(e) => setNomeModello(e.target.value)}
                  placeholder="Nome del modello, es. Iscrizione campus"
                />
              )}
            </div>
          )}
        </div>
      )}

      {fase === 'fatto' && linkCreato && (
        <div className="space-y-3">
          <p className="text-sm text-[#37352F] dark:text-gray-200">
            Manda questo collegamento al cliente: da lì carica i documenti quando vuole.
          </p>
          <div className="flex items-center gap-2">
            <input readOnly value={linkCreato} className={inputDocumentiCls} onFocus={(e) => e.target.select()} />
            <button
              type="button"
              onClick={() => void copia()}
              className="flex items-center gap-1 rounded-md border border-[rgba(55,53,47,0.16)] px-2.5 py-1.5 text-sm text-[#37352F] hover:bg-[rgba(55,53,47,0.06)] dark:text-gray-200"
            >
              {copiato ? <Check className="h-3.5 w-3.5 text-[#0F7B6C]" /> : <Copy className="h-3.5 w-3.5" />}
              {copiato ? 'Copiato' : 'Copia'}
            </button>
          </div>
          <p className="text-xs text-[#787774]">
            Lo ritrovi anche qui nella scheda, sotto "Documenti richiesti".
            {salvaComeModello && nomeModello && ` Il modello "${nomeModello.trim()}" è pronto per la prossima volta.`}
          </p>
        </div>
      )}
    </NotionDrawer>
  );
}
